/**
 * Portal público: enlaces coherentes con el embudo membresía (/registro → perfil → pendiente → acceso).
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSEO } from "@/hooks/useSEO";
import { BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_SUPPORT_EMAIL } from "@/lib/brandConfig";
import {
  MEMBERSHIP_ACCESS_PORTAL_PATH,
  MEMBERSHIP_ROUTE,
} from "@/lib/membershipAccessFlow";
import { MEMBERSHIP_PORTAL_STEPS } from "@/lib/membershipPortalSteps";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  CreditCard,
  LogIn,
  Sparkles,
  UserPlus,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

const ICON_BY_PATH: Record<string, LucideIcon> = {
  [MEMBERSHIP_ROUTE.REGISTRO]: CreditCard,
  [MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO]: UserPlus,
  [MEMBERSHIP_ROUTE.PENDING_APPROVAL]: Clock,
  [MEMBERSHIP_ROUTE.MI_ACCESO]: CheckCircle2,
  [MEMBERSHIP_ROUTE.ENTRAR]: LogIn,
};

const SEO = {
  title: `Pasos de acceso a la membresía — ${BRAND_PERSON_NAME}`,
  description: `Después del pago: crear cuenta, completar perfil, esperar la aprobación del equipo y obtener el link de ingreso.`,
  keywords: "",
  ogImage: `${BRAND_SITE_URL}/og.png`,
  canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ACCESS_PORTAL_PATH}`,
  robots: "noindex, nofollow" as const,
};

export default function MembershipAccessPortal() {
  useSEO(SEO);

  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : (BRAND_SITE_URL || "").replace(/\/$/, "");

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 py-10 md:py-14">
      <div className="mx-auto max-w-2xl space-y-8">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-medium text-amber-200 ring-1 ring-amber-500/30">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Membresía
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-zinc-50">
            Pasos para acceder a tu membresía
          </h1>
          <p className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
            Mismo orden que después del pago en {BRAND_PERSON_NAME || "este sitio"}: elegí la opción que coincide con tu situación.
          </p>
        </div>

        <ol className="space-y-4 list-none p-0 m-0">
          {MEMBERSHIP_PORTAL_STEPS.map((step, index) => {
            const Icon = ICON_BY_PATH[step.path] ?? UserPlus;
            const highlight = step.highlight === true;
            return (
              <li key={step.path}>
                <Card
                  className={`border-white/10 bg-zinc-900/70 backdrop-blur ${
                    highlight ? "ring-1 ring-amber-500/25 shadow-lg shadow-amber-900/10" : ""
                  }`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start gap-3">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums ${
                          highlight
                            ? "bg-amber-500/25 text-amber-100 ring-1 ring-amber-500/35"
                            : "bg-white/10 text-zinc-300"
                        }`}
                        aria-hidden
                      >
                        {index + 1}
                      </div>
                      <div
                        className={`rounded-lg p-2 shrink-0 ${
                          highlight ? "bg-amber-500/20 text-amber-200" : "bg-white/5 text-zinc-300"
                        }`}
                      >
                        <Icon className="h-5 w-5" aria-hidden />
                      </div>
                      <div className="min-w-0 space-y-1">
                        <CardTitle className="text-base text-zinc-100 leading-snug">{step.title}</CardTitle>
                        <CardDescription className="text-zinc-400 text-sm leading-relaxed">{step.body}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Button
                      asChild
                      className={
                        highlight
                          ? "w-full sm:w-auto bg-amber-500 text-zinc-950 hover:bg-amber-400 font-semibold"
                          : "w-full sm:w-auto bg-white text-zinc-950 hover:bg-white/90 font-semibold"
                      }
                    >
                      <Link to={step.path} className="gap-2 inline-flex items-center justify-center">
                        {step.cta}
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </Link>
                    </Button>
                    <p className="mt-2 text-[11px] font-mono text-zinc-500 tabular-nums break-all">
                      {origin}
                      {step.path}
                    </p>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ol>

        {BRAND_SUPPORT_EMAIL ? (
          <p className="text-center text-xs text-zinc-500">
            ¿Seguís con dudas? Escribinos a{" "}
            <a
              href={`mailto:${BRAND_SUPPORT_EMAIL}`}
              className="text-amber-300/90 underline underline-offset-2 hover:text-amber-200"
            >
              {BRAND_SUPPORT_EMAIL}
            </a>{" "}
            con el correo con el que pagaste.
          </p>
        ) : null}

        <p className="text-center">
          <Link to="/" className="text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-2">
            Volver al inicio
          </Link>
        </p>
      </div>
    </div>
  );
}
